import { type ReactNode } from "react";
import { type ColumnDef } from "@tanstack/react-table";
import { AlertTriangle } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import {
  formatDate,
  formatEurCompact,
  formatNumber,
  initials,
} from "@/lib/formatters";
import { isMAProject } from "@/lib/projectConfig";
import { CFO_ACTIONS } from "@/lib/cfoActions";
import { useCurrentProject } from "@/hooks/useCurrentProject";
import { HiddenDetail, isHidden } from "@/components/shared/HiddenDetail";
import type {
  AvailableColumn,
  Company,
  ProjectMember,
  User,
} from "@/api/types";

/**
 * Reads a Company value for a generic column. metadata-backed columns
 * read from row.metadata[metadata_key]; everything else is a first-class
 * attribute on Company. contacts_count is handled by its own column
 * factory above (it needs primary_contact alongside the count).
 */
export function readCompanyValue(
  row: Company,
  cfg: AvailableColumn,
): unknown {
  if (cfg.source === "metadata" && cfg.metadata_key) {
    return row.metadata?.[cfg.metadata_key];
  }
  return (row as unknown as Record<string, unknown>)[cfg.key];
}

/**
 * Builds a TanStack ColumnDef for a backend-supplied column key that
 * doesn't have a dedicated rich renderer in this table (e.g. headcount,
 * description, founder_origin). The cell formatter picks a small format
 * based on the column's `type`. user-typed columns are resolved through
 * `members` (or currentUser) so generic UUID columns render as
 * avatar + name instead of the raw UUID. The contacts_count key gets a
 * dedicated rich cell that surfaces the primary contact's name + DM
 * badge alongside the count, since the backend now ships both
 * contacts_count and primary_contact on every row.
 */
export function buildGenericCompanyColumn(
  key: string,
  availableColumns: AvailableColumn[] | undefined,
  members: ProjectMember[],
  currentUser: User | null,
): ColumnDef<Company> | null {
  const cfg = availableColumns?.find((c) => c.key === key);
  if (!cfg) return null;
  // Generous default size + broad bounds — backend columns vary
  // wildly (numeric vs short labels vs long paragraphs), so we let
  // the user drag to fit rather than guessing per-type widths.
  const sizing = { size: 140, minSize: 60, maxSize: 320 };
  if (cfg.key === "contacts_count") {
    return {
      id: cfg.key,
      header: cfg.label,
      accessorFn: (row) => row.contacts_count ?? 0,
      cell: ({ row }) => <ContactsCountCell company={row.original} />,
      enableSorting: false,
      ...sizing,
    };
  }
  if (cfg.key === "cfo_action") {
    return {
      id: cfg.key,
      header: cfg.label,
      accessorFn: (row) => readCompanyValue(row, cfg),
      cell: ({ getValue }) => <CfoActionCell value={getValue()} />,
      enableSorting: false,
      ...sizing,
      size: 130,
    };
  }
  return {
    id: cfg.key,
    header: cfg.label,
    accessorFn: (row) => readCompanyValue(row, cfg),
    cell: ({ getValue, row }) =>
      // The company phone, withheld outside the reader's call session.
      isHidden(row.original, cfg.key) ? (
        <HiddenDetail label="Phone" className="text-xs" />
      ) : (
        renderGenericCompanyCell(getValue(), cfg.type, members, currentUser)
      ),
    enableSorting: false,
    ...sizing,
  };
}

function CfoActionCell({ value }: { value: unknown }) {
  if (typeof value !== "string" || value === "") {
    return <span className="text-xs text-slate-400">—</span>;
  }
  const action = CFO_ACTIONS[value];
  return (
    <span
      title={action?.hint}
      className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800"
    >
      <AlertTriangle className="h-3 w-3 shrink-0" />
      {action?.label ?? value}
    </span>
  );
}

function ContactsCountCell({ company }: { company: Company }) {
  // Pull project type from the store rather than threading an isMA
  // prop through CompanyTable / ClientsTable and their column
  // builders. Cheap — useCurrentProject reads from Zustand with no
  // network.
  const { project } = useCurrentProject();
  const isMA = isMAProject(project);

  const count = company.contacts_count ?? 0;
  const primary = company.primary_contact ?? null;
  if (!count) return <span className="text-xs text-slate-400">—</span>;
  // No primary contact carried back yet (older listings) — fall back to
  // a plain count so the cell stays useful.
  if (!primary) {
    return (
      <span className="text-sm tabular-nums text-slate-700">
        {formatNumber(count)}
      </span>
    );
  }
  const initial = primary.full_name?.charAt(0)?.toUpperCase() ?? "?";
  const ageRaw = primary.metadata?.age;
  const age = typeof ageRaw === "number" && Number.isFinite(ageRaw)
    ? ageRaw
    : null;
  return (
    <div className="flex items-center gap-2">
      <div className="flex min-w-0 items-center gap-1.5">
        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-100 text-[10px] font-medium text-slate-500">
          {initial}
        </span>
        <span className="max-w-[140px] truncate text-sm text-slate-700">
          {primary.full_name ?? primary.email ?? "—"}
        </span>
        {primary.is_decision_maker && (
          <span className="rounded bg-indigo-50 px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-indigo-700">
            DM
          </span>
        )}
        {isMA && age != null && (
          <span
            className={cn(
              "text-[10px] font-medium tabular-nums",
              // 65+ is the strong governance-succession signal in the
              // M&A scoring tranches; lift it visually so it pops
              // even at glance distance.
              age >= 65 ? "text-emerald-600" : "text-slate-400",
            )}
          >
            {age} ans
          </span>
        )}
      </div>
      {count > 1 && (
        <span className="shrink-0 text-xs text-slate-400">+{count - 1}</span>
      )}
    </div>
  );
}

export function renderGenericCompanyCell(
  value: unknown,
  type: string,
  members: ProjectMember[],
  currentUser: User | null,
): ReactNode {
  if (value == null || value === "") {
    return <span className="text-xs text-slate-400">—</span>;
  }
  switch (type) {
    case "number":
      return (
        <span className="text-sm tabular-nums text-slate-700">
          {typeof value === "number" ? formatNumber(value) : String(value)}
        </span>
      );
    case "currency":
    // Forward-compat: the backend's ColumnRenderType is "currency"
    // today, but the openapi field is free-form ("a new render hint
    // from the backend doesn't break the type"). Accept the more
    // specific "currency_eur" too so a future backend split doesn't
    // silently revert these cells to the default text rendering.
    case "currency_eur":
      return (
        <span className="text-sm tabular-nums text-slate-700">
          {typeof value === "number"
            ? formatEurCompact(value)
            : String(value)}
        </span>
      );
    case "percent": {
      const n = typeof value === "number" ? value : Number(value);
      return (
        <span className="text-sm tabular-nums text-slate-700">
          {Number.isFinite(n) ? `${n.toFixed(1)}%` : String(value)}
        </span>
      );
    }
    case "date":
      return (
        <span className="text-xs text-slate-500">
          {typeof value === "string" ? formatDate(value) : String(value)}
        </span>
      );
    case "badge":
      return (
        <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-medium capitalize text-slate-600">
          {String(value).replace(/_/g, " ")}
        </span>
      );
    case "user": {
      if (typeof value !== "string") {
        return <span className="text-xs text-slate-400">—</span>;
      }
      const member = members.find((m) => m.id === value);
      const fullName =
        member?.full_name ??
        (currentUser?.id === value ? currentUser?.full_name : null);
      if (!fullName) {
        return (
          <span className="text-xs text-slate-400" title={value}>
            Unknown user
          </span>
        );
      }
      return (
        <div className="flex items-center gap-2">
          <Avatar className={cn("h-6 w-6 border border-slate-200")}>
            <AvatarFallback className="bg-slate-900 text-[10px] font-semibold text-white">
              {initials(fullName)}
            </AvatarFallback>
          </Avatar>
          <span className="text-sm text-slate-600">{fullName}</span>
        </div>
      );
    }
    case "text":
    default:
      return (
        <span className="block max-w-[260px] truncate text-sm text-slate-700">
          {String(value)}
        </span>
      );
  }
}

/**
 * Common id → backend-key alias map for the company-shaped tables
 * (CompanyTable, ClientsTable). Each table registers its own existing
 * columns under their ids first, then this map adds the canonical
 * backend keys so a catalog like ["name", "owner_id", "updated_at"]
 * resolves to the same rich renderers as ["company", "owner",
 * "updated"].
 */
export const COMPANY_COLUMN_ALIASES: Record<string, readonly string[]> = {
  company: ["name"],
  score: ["effective_score"],
  status: ["pipeline_status"],
  owner: ["owner_id"],
  updated: ["updated_at"],
  archived: ["archived_at"],
};
